import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Plus, Edit2, Trash2, Search, Clock, Users, Coffee } from "lucide-react";
import { Badge } from "../ui/badge";
import { ShiftDialog } from "./ShiftDialog";
import { toast } from "sonner";
import { GET_SHIFTS } from "../../graphql/queries";
import {
  CREATE_SHIFT,
  UPDATE_SHIFT,
  DELETE_SHIFT,
} from "../../graphql/mutations";
import { usePermission } from "../../hooks/usePermission";

interface ShiftTime {
  id: string;
  startTime: string;
  endTime: string;
  daysOfWeek: string[];
  order: number;
  autoBreak: boolean;
  autoBreakStartTime?: string;
  autoBreakEndTime?: string;
}

interface Shift {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  lateArrivalToleranceMinutes: number;
  earlyDepartureToleranceMinutes: number;
  earlyArrivalToleranceMinutes: number;
  lateDepartureToleranceMinutes: number;
  shiftTimes: ShiftTime[];
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
}

interface ShiftManagementProps {
  currentUserRole?: string;
}

const DAYS_MAP: Record<string, string> = {
  MON: 'Pn',
  TUE: 'Wt',
  WED: 'Śr',
  THU: 'Czw',
  FRI: 'Pt',
  SAT: 'Sob',
  SUN: 'Ndz',
};

export const ShiftManagement: React.FC<ShiftManagementProps> = ({
  currentUserRole,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);

  // Fetch shifts
  const { data: shiftsData, loading: shiftsLoading, refetch: refetchShifts } = useQuery(GET_SHIFTS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("✅ Shifts loaded:", data.shifts?.length);
    },
    onError: (error) => {
      console.error("❌ Error loading shifts:", error);
      toast.error("Błąd podczas ładowania zmianowości");
    },
  });

  const [createShift] = useMutation(CREATE_SHIFT);
  const [updateShift] = useMutation(UPDATE_SHIFT);
  const [deleteShift] = useMutation(DELETE_SHIFT);

  const shifts = shiftsData?.shifts || [];

  // Filter shifts based on search
  const filteredShifts = shifts.filter((shift: Shift) =>
    shift.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (shift.description && shift.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSave = async (id: string | null, data: any) => {
    try {
      if (id) {
        await updateShift({
          variables: { id, data },
        });
        toast.success("Zmianowość została zaktualizowana");
      } else {
        await createShift({
          variables: { data },
        });
        toast.success("Zmianowość została dodana");
      }
      await refetchShifts();
      setDialogOpen(false);
    } catch (error: any) {
      console.error("Error saving shift:", error);
      throw new Error(error.message || "Błąd podczas zapisywania zmianowości");
    }
  };

  const handleAddNew = () => {
    setSelectedShift(null);
    setDialogOpen(true);
  };

  const handleEdit = (shift: Shift) => {
    setSelectedShift(shift);
    setDialogOpen(true);
  };

  const handleDelete = async (shift: Shift) => {
    if (shift.isDefault) {
      toast.error("Nie można usunąć domyślnej zmianowości");
      return;
    }

    if (shift.employeeCount > 0) {
      toast.error(`Nie można usunąć zmianowości, która ma przypisanych ${shift.employeeCount} pracowników`);
      return;
    }

    const confirmed = window.confirm(
      `Czy na pewno chcesz usunąć zmianowość "${shift.name}"?`
    );

    if (confirmed) {
      try {
        await deleteShift({
          variables: { id: shift.id },
        });
        toast.success(`Zmianowość "${shift.name}" została usunięta`);
        await refetchShifts();
      } catch (error: any) {
        console.error("Error deleting shift:", error);
        toast.error(error.message || "Nie udało się usunąć zmianowości");
      }
    }
  };

  // Legacy variable name kept for minimal diff — value now derived from permission system.
  const isAdmin = usePermission("shifts.manage");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Zmianowość</h1>
        <p className="text-muted-foreground">
          Zarządzaj zmianami pracy i przedziałami czasowymi
        </p>
      </div>

      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Wszystkie zmiany</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{shifts.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aktywne</CardTitle>
            <Clock className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {shifts.filter((s: Shift) => s.isActive).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Domyślna</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {shifts.filter((s: Shift) => s.isDefault).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Przypisani pracownicy</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {shifts.reduce((acc: number, s: Shift) => acc + s.employeeCount, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle>Lista zmianowości</CardTitle>
              <CardDescription>
                {filteredShifts.length} {filteredShifts.length === 1 ? 'zmianowość' : 'zmianowości'}
              </CardDescription>
            </div>
            {isAdmin && (
              <Button onClick={handleAddNew}>
                <Plus className="mr-2 h-4 w-4" />
                Dodaj zmianowość
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Szukaj zmianowości..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          {/* List */}
          {shiftsLoading ? (
            <div className="text-center py-8">
              <div className="text-muted-foreground">Ładowanie...</div>
            </div>
          ) : filteredShifts.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-muted-foreground">
                {searchTerm ? 'Brak wyników wyszukiwania' : 'Brak zmianowości'}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredShifts.map((shift: Shift) => (
                <Card key={shift.id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold">{shift.name}</h3>
                          <div className="flex gap-2">
                            {shift.isDefault && (
                              <Badge variant="default">Domyślna</Badge>
                            )}
                            {shift.isActive ? (
                              <Badge variant="outline" className="text-green-600">
                                Aktywna
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">
                                Nieaktywna
                              </Badge>
                            )}
                            {shift.employeeCount > 0 && (
                              <Badge variant="secondary">
                                <Users className="w-3 h-3 mr-1" />
                                {shift.employeeCount}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {shift.description && (
                          <p className="text-sm text-muted-foreground">
                            {shift.description}
                          </p>
                        )}

                        {/* Shift Times */}
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-muted-foreground">
                            Przedziały czasowe:
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {shift.shiftTimes.map((st, idx) => (
                              <div key={idx} className="flex items-center gap-1">
                                <Badge variant="outline" className="font-mono">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {st.startTime} - {st.endTime}
                                  {st.daysOfWeek.length > 0 && (
                                    <span className="ml-2 text-xs">
                                      ({st.daysOfWeek.map(d => DAYS_MAP[d]).join(', ')})
                                    </span>
                                  )}
                                </Badge>
                                {st.autoBreak && (
                                  <Badge variant="secondary" className="text-orange-600 font-mono">
                                    <Coffee className="w-3 h-3 mr-1" />
                                    {st.autoBreakStartTime}–{st.autoBreakEndTime}
                                  </Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Tolerances */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                          <div>
                            <span className="font-medium">Spóźnienie:</span> {shift.lateArrivalToleranceMinutes}min
                          </div>
                          <div>
                            <span className="font-medium">Wcześniejsze wyjście:</span> {shift.earlyDepartureToleranceMinutes}min
                          </div>
                          <div>
                            <span className="font-medium">Wcześniejsze wejście:</span> {shift.earlyArrivalToleranceMinutes}min
                          </div>
                          <div>
                            <span className="font-medium">Późniejsze wyjście:</span> {shift.lateDepartureToleranceMinutes}min
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      {isAdmin && (
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(shift)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(shift)}
                            disabled={shift.isDefault || shift.employeeCount > 0}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      {isAdmin && (
        <ShiftDialog
          shift={selectedShift}
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setSelectedShift(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};
